import type { FC } from 'react'

import { useRouter } from 'next/router'
import PropTypes from 'prop-types'

import { Currency, Hr, LeaseTerm, SocialSharing, Span, Text } from 'components'
import { PLACEHOLDER as placeHolderData } from 'containers/agent-profile/contact-info/contact-agent/email-modal/constants'
import { ContactAgentPrescreenWizard } from 'containers/agent-profile/contact-info/contact-agent/prescreen-wizard/contact-agent-prescreen-wizard'
import { ModalProvider } from 'context/modalContext'
import env from 'env'
import { COLOR } from 'styles/constants/color'
import { interpolateMessage } from 'utils/interpolate-message'

import { useApplyButtonState, useApplyPanelData } from './hook'
import {
  StyledBodySection,
  StyledHeaderSection,
  StyledCard,
  StyledApplyButton,
  StyledContactButton,
  StyledButtonGroup,
} from './styled'

type ApplyPanelProps = IElement & {
  onApplyClick: (e: React.MouseEvent) => void
  applyButtonId: string
  contactButtonId: string
  isSticky?: boolean
  isPreview?: boolean
  disableShare?: boolean
  onContactClick: (e: React.MouseEvent) => void
  profile?: AgentProfile
}

type ContactButtonProps = {
  contactButtonId: string
  isPreview: boolean
  onClick?: (e: React.MouseEvent) => void
}

const ContactButton: FC<ContactButtonProps> = ({ contactButtonId, isPreview, onClick }) => (
  <StyledContactButton
    id={contactButtonId}
    disabled={isPreview}
    onClick={!isPreview ? onClick : undefined}
    as="button"
  >
    <Span
      $weight="600"
      $size="16px"
      $lineHeight="28px"
      $color={!isPreview ? COLOR.GREY_LV1 : COLOR.GREY_LV3}
    >
      Contact
    </Span>
  </StyledContactButton>
)

export const ApplyPanel: React.FC<ApplyPanelProps> = ({
  onApplyClick,
  applyButtonId,
  contactButtonId,
  isSticky = false,
  isPreview = false,
  disableShare = false,
  onContactClick,
  profile,
  ...style
}) => {
  const {
    rentAmount,
    securityDeposit,
    termAmountOfMonths,
    term,
    dateAvailable,
    displayName,
    textAddress,
    propertyId,
  } = useApplyPanelData()
  const router = useRouter()
  const { text: applyButtonText, disabled: isApplyButtonDisabled } = useApplyButtonState()

  const messageTemplate = placeHolderData.MESSAGE

  return (
    <>
      <StyledCard isSticky={isSticky} {...style}>
        <StyledHeaderSection>
          <Span
            $weight={isSticky ? '400' : '500'}
            $size={isSticky ? '14px' : '16px'}
            $lineHeight="24px"
          >
            Rent Amount
          </Span>
          <Currency
            $weight="700"
            $size={isSticky ? '14px' : '22px'}
            $lineHeight={isSticky ? '24px' : '40px'}
            value={rentAmount}
          />
        </StyledHeaderSection>
        {isSticky ? null : <Hr $mt={20} $opacity="1" />}
        <StyledBodySection>
          <Span>Security Deposit</Span>
          <Currency value={securityDeposit} />
          <Span>Lease Term</Span>
          <LeaseTerm term={term} termAmountOfMonths={termAmountOfMonths} />
          <Span>Date Available</Span>
          <Span>{dateAvailable}</Span>
        </StyledBodySection>
        <StyledButtonGroup>
          <StyledApplyButton
            disabled={isApplyButtonDisabled || isPreview}
            id={applyButtonId}
            onClick={onApplyClick}
          >
            <Text
              $size="16px"
              $bold
              $lineHeight="28px"
              $color={isApplyButtonDisabled || isPreview ? COLOR.GREY_LV3 : COLOR.WHITE}
            >
              {applyButtonText}
            </Text>
          </StyledApplyButton>
          <ModalProvider>
            {({ toggleModal, closeModal }) => {
              const handleClickContact = (e) => {
                onContactClick(e)
                toggleModal(
                  <ContactAgentPrescreenWizard
                    agentFullName={displayName}
                    defaultMessage={interpolateMessage(messageTemplate, {
                      agent_name: displayName,
                      rental_property_address: textAddress,
                    })}
                    property={{
                      address: textAddress,
                      id: propertyId,
                    }}
                    profile={profile}
                    onModalClose={closeModal}
                  />,
                )
              }

              return (
                <ContactButton
                  contactButtonId={contactButtonId}
                  isPreview={isPreview}
                  onClick={handleClickContact}
                />
              )
            }}
          </ModalProvider>
        </StyledButtonGroup>
      </StyledCard>
      {!disableShare && (
        <SocialSharing
          url={`${env?.BASE_URL}/listing${router?.asPath}`}
          propertyAddress={textAddress}
          show={['facebook', 'twitter', 'linkedin', 'email']}
          disable={isPreview}
        />
      )}
    </>
  )
}

ApplyPanel.propTypes = {
  onApplyClick: PropTypes.func.isRequired,
  applyButtonId: PropTypes.string.isRequired,
  contactButtonId: PropTypes.string.isRequired,
  onContactClick: PropTypes.func.isRequired,
  isSticky: PropTypes.bool,
  isPreview: PropTypes.bool,
  disableShare: PropTypes.bool,
  profile: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool])),
}

ApplyPanel.defaultProps = {
  isSticky: false,
  isPreview: false,
  disableShare: false,
  profile: {},
}
